import type { CV, CVSummary, CreateCVRequest, PDFOptions, UpdateCVRequest } from './cv'

/**
 * Platform adapter interface
 * All platform integrations must implement this interface
 */
export interface PlatformAdapter {
  storage: CVStorageAdapter
  auth: AuthAdapter
  config: PlatformConfig
}

/**
 * CV Storage operations
 */
export interface CVStorageAdapter {
  /** List all CVs for the current user */
  listCVs(): Promise<CVSummary[]>

  /** Get a single CV by ID */
  getCV(id: string): Promise<CV>

  /** Create a new CV */
  createCV(data?: CreateCVRequest): Promise<CV>

  /** Update an existing CV */
  updateCV(id: string, data: UpdateCVRequest): Promise<CV>

  /** Delete a CV */
  deleteCV(id: string): Promise<void>

  /** Generate PDF from a CV */
  generatePDF(id: string, options?: PDFOptions): Promise<Blob>

  /** Upload a photo and return the URL */
  uploadPhoto(file: File): Promise<string>
}

/**
 * Authentication adapter
 */
export interface AuthAdapter {
  /** Check if user is authenticated */
  isAuthenticated(): boolean

  /** Get current user info */
  getUser(): User | null

  /** Get access token for API calls */
  getAccessToken(): Promise<string | null>

  /** Trigger login flow */
  login(): Promise<void>

  /** Trigger logout */
  logout(): Promise<void>

  /** Subscribe to auth state changes */
  onAuthStateChange(callback: (user: User | null) => void): () => void
}

/**
 * User info
 */
export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string
}

/**
 * Platform configuration
 */
export interface PlatformConfig {
  /** Platform identifier */
  platform: 'virtual-hive' | 'standalone' | 'demo' | string

  /** Locale defaults and supported languages */
  locale?: LocalizationConfig

  /** Available templates */
  templates: TemplateConfig[]

  /** Branding customization */
  branding?: BrandingConfig

  /** Feature flags */
  features?: FeatureFlags
}

/**
 * Locale configuration for UI and CV document output
 */
export interface LocalizationConfig {
  /** Default builder surface language */
  defaultUiLocale?: string

  /** Default language for new CV documents */
  defaultCvLocale?: string

  /** Supported UI/CV locales */
  supportedLocales?: string[]
}

/**
 * Template configuration
 */
export interface TemplateConfig {
  id: string
  name: string
  description: string
  previewUrl: string
  isPremium?: boolean
}

/**
 * Branding configuration for white-label deployments
 */
export interface BrandingConfig {
  logo?: string
  logoAlt?: string
  /** Overrides the app title in the header (e.g. the embedding hive's name). */
  title?: string
  primaryColor?: string
  accentColor?: string
  fontFamily?: string
  /** Top bar (header) background — e.g. the hive theme color. */
  topbarColor?: string
  /** Top bar text/foreground color. */
  topbarTextColor?: string
}

/**
 * Feature flags
 */
export interface FeatureFlags {
  /** Enable photo upload */
  photoUpload?: boolean

  /** Enable PDF export */
  pdfExport?: boolean

  /** Enable multiple CVs per user */
  multipleCVs?: boolean

  /** Enable custom sections */
  customSections?: boolean

  /** Enable AI-assisted content */
  aiAssist?: boolean
}

/**
 * Iframe communication (for embedded scenarios)
 */
export interface IframeAdapter {
  /** Post message to parent window */
  postMessage(message: IframeMessage): void

  /** Subscribe to messages from parent window */
  onMessage(handler: (message: IframeMessage) => void): () => void
}

export interface IframeMessage {
  type: string
  payload?: unknown
}

/**
 * Default platform config
 */
export const defaultPlatformConfig: PlatformConfig = {
  platform: 'demo',
  locale: {
    defaultUiLocale: 'da-DK',
    defaultCvLocale: 'da-DK',
    supportedLocales: ['da-DK', 'en-US'],
  },
  templates: [
    {
      id: 'classic',
      name: 'Klassisk',
      description: 'Et rent og professionelt layout',
      previewUrl: '/templates/classic.png',
    },
    {
      id: 'modern',
      name: 'Moderne',
      description: 'Nutidigt design med accentfarver',
      previewUrl: '/templates/modern.png',
    },
    {
      id: 'minimal',
      name: 'Minimal',
      description: 'Enkelt og elegant',
      previewUrl: '/templates/minimal.png',
    },
    {
      id: 'sidebar-left',
      name: 'Sidebar venstre',
      description: 'Foto og kompetencer i venstre side',
      previewUrl: '/templates/sidebar-left.png',
    },
    {
      id: 'sidebar-right',
      name: 'Sidebar højre',
      description: 'Foto og kompetencer i højre side',
      previewUrl: '/templates/sidebar-right.png',
    },
  ],
  features: {
    photoUpload: true,
    pdfExport: true,
    multipleCVs: true,
    customSections: true,
    aiAssist: false,
  },
}

export function mergePlatformConfig(
  defaultConfig: PlatformConfig,
  overrides?: Partial<PlatformConfig>
): PlatformConfig {
  if (!overrides) return defaultConfig

  const mergedConfig: PlatformConfig = {
    ...defaultConfig,
    ...overrides,
    templates: overrides.templates ?? defaultConfig.templates,
  }

  if (defaultConfig.locale || overrides.locale) {
    mergedConfig.locale = {
      ...(defaultConfig.locale ?? {}),
      ...(overrides.locale ?? {}),
    }
  }

  if (defaultConfig.branding || overrides.branding) {
    mergedConfig.branding = {
      ...(defaultConfig.branding ?? {}),
      ...(overrides.branding ?? {}),
    }
  }

  if (defaultConfig.features || overrides.features) {
    mergedConfig.features = {
      ...(defaultConfig.features ?? {}),
      ...(overrides.features ?? {}),
    }
  }

  return mergedConfig
}
